import { useEffect, useState, useCallback } from "react";
import { usersAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { BranchBadge, BranchTabs, useBranchSplit } from "../components/ui/BranchTabs";
import toast from "react-hot-toast";
import { format } from "date-fns";

const ROLES = ["customer","rider","branch_manager","admin","super_admin"];

const roleBadge = (r) => {
  const map = { super_admin:"badge-cancelled", admin:"badge-b2b", branch_manager:"badge-on-way", rider:"badge-active", customer:"badge-pending" };
  return <span className={`badge ${map[r]||"badge-pending"}`}>{r?.replace(/_/g," ")}</span>;
};

const blank = { name:"", phone:"", email:"", password:"", role:"customer", branch_id:"", status:"active" };

export default function Users() {
  const { user: me } = useAuth();
  const isSuperAdmin  = me?.role === "super_admin";
  const isAdmin       = ["admin","super_admin"].includes(me?.role);
  const isBranchMgr   = me?.role === "branch_manager";
  const availableRoles = isSuperAdmin ? ROLES : isAdmin ? ["customer","rider","branch_manager","admin"] : ["customer","rider"];

  const [users, setUsers]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(blank);
  const [search, setSearch]   = useState("");
  const [roleFilter, setRole] = useState("");
  const [saving, setSaving]   = useState(false);
  const [tab, setTab]         = useState("mine");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await usersAPI.list({ page, limit: 50, search, role: roleFilter });
      setUsers(r.data.data || []); setTotal(r.data.pagination?.total || 0);
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  }, [page, search, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const { mine, others, visible } = useBranchSplit(users, isBranchMgr ? tab : "all");

  const openCreate = () => { setForm(blank); setEditing(null); setModal("create"); };
  const openEdit   = (u)  => { setForm({...u, password:""}); setEditing(u); setModal("edit"); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === "create") { await usersAPI.create(form); toast.success("User created"); }
      else { await usersAPI.update(editing.id, form); toast.success("User updated"); }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await usersAPI.delete(u.id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const pages = Math.ceil(total / 50);

  return (
    <div>
      <style>{`
        .users-mobile-cards  { display: none; }
        .users-desktop-table { display: block; }
        .user-card {
          padding: 14px 16px;
          border-bottom: 1px solid var(--border);
        }
        .user-card:last-child { border-bottom: none; }
        @media (max-width: 768px) {
          .users-mobile-cards  { display: block; }
          .users-desktop-table { display: none; }
        }
      `}</style>

      <div className="page-header">
        <div>
          <div className="page-title">Users</div>
          <div className="page-subtitle">{total.toLocaleString()} total users</div>
        </div>
        {isAdmin && <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> New User</button>}
      </div>

      <BranchTabs activeTab={tab} onChange={setTab} myCount={mine.length} otherCount={others.length} />

      {isBranchMgr && tab === "others" && (
        <div style={{display:"flex",alignItems:"center",gap:10,background:"rgba(90,90,114,0.1)",border:"1px solid rgba(90,90,114,0.2)",borderRadius:10,padding:"10px 16px",marginBottom:16,fontSize:13}}>
          <span style={{fontSize:18}}>🏢</span>
          <span style={{color:"var(--text2)"}}>
            These users belong to <strong style={{color:"var(--text)"}}>other branches</strong>.
            You can view their details to help them, but cannot edit their accounts.
          </span>
        </div>
      )}

      <div className="filters-bar">
        <div style={{position:"relative",flex:1,minWidth:0}}>
          <Search size={14} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--text3)"}}/>
          <input className="input search-input" style={{paddingLeft:32,width:"100%"}} placeholder="Search name, phone, email..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}/>
        </div>
        <select className="select" value={roleFilter} onChange={e=>{setRole(e.target.value);setPage(1);}}>
          <option value="">All roles</option>
          {ROLES.map(r=><option key={r} value={r}>{r.replace(/_/g," ")}</option>)}
        </select>
      </div>

      <div className="card">
        {loading
          ? <div className="loading-center"><div className="spinner"/><span>Loading...</span></div>
          : (
          <>
            {/* Mobile user cards */}
            <div className="users-mobile-cards">
              {visible.length === 0
                ? <div className="empty-state"><p>No users found</p></div>
                : visible.map(u => {
                  const isProtected  = u.role === "super_admin" && !isSuperAdmin;
                  const isForeign    = isBranchMgr && !u.is_my_branch;
                  const canEdit      = isAdmin && !isProtected && !isForeign;
                  const canDelete    = isSuperAdmin;
                  return (
                    <div key={u.id} className="user-card" style={{opacity:isProtected?0.5:1,background:isForeign?"rgba(90,90,114,0.04)":undefined}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                        <div style={{
                          width:36,height:36,borderRadius:8,flexShrink:0,
                          background:isForeign?"linear-gradient(135deg,#5a5a72,#3a3a52)":"linear-gradient(135deg,var(--accent),var(--accent2))",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          fontWeight:700,fontSize:14,color:"#000"
                        }}>
                          {u.name?.[0]?.toUpperCase()}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:14}}>{u.name}</div>
                          <div style={{fontSize:11,color:"var(--text3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email||u.phone||"—"}</div>
                        </div>
                        <div style={{display:"flex",gap:4}}>
                          {canEdit   && <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>openEdit(u)}><Pencil size={13}/></button>}
                          {canDelete && <button className="btn btn-danger btn-icon btn-sm" onClick={()=>handleDelete(u)}><Trash2 size={13}/></button>}
                        </div>
                      </div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                        {roleBadge(u.role)}
                        <span className={`badge ${u.status==="active"?"badge-active":u.status==="suspended"?"badge-suspended":"badge-pending"}`}>{u.status}</span>
                        <BranchBadge branchName={u.branch_name||(u.branch_id?`Branch #${u.branch_id}`:null)} isMyBranch={!!u.is_my_branch} compact/>
                        {u.created_at && <span style={{fontSize:11,color:"var(--text3)",marginLeft:"auto"}}>{format(new Date(u.created_at),"MMM d, yyyy")}</span>}
                      </div>
                    </div>
                  );
                })
              }
            </div>

            {/* Desktop table */}
            <div className="users-desktop-table table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th><th>Phone</th><th>Email</th><th>Role</th>
                    <th>Branch</th><th>Status</th><th>Joined</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0
                    ? <tr><td colSpan={8}><div className="empty-state"><p>No users found</p></div></td></tr>
                    : visible.map(u => {
                      const isProtected  = u.role === "super_admin" && !isSuperAdmin;
                      const isForeign    = isBranchMgr && !u.is_my_branch;
                      const canEdit      = isAdmin && !isProtected && !isForeign;
                      const canDelete    = isSuperAdmin;
                      return (
                        <tr key={u.id} style={{
                          opacity: isProtected ? 0.5 : 1,
                          background: isForeign ? "rgba(90,90,114,0.04)" : undefined,
                        }}>
                          <td>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <div style={{
                                width:30,height:30,borderRadius:7,flexShrink:0,
                                background: isForeign
                                  ? "linear-gradient(135deg,#5a5a72,#3a3a52)"
                                  : "linear-gradient(135deg,var(--accent),var(--accent2))",
                                display:"flex",alignItems:"center",justifyContent:"center",
                                fontWeight:700,fontSize:12,color:"#000"
                              }}>
                                {u.name?.[0]?.toUpperCase()}
                              </div>
                              <div>
                                <div className="fw-bold">{u.name}</div>
                                {isProtected && <div style={{fontSize:10,color:"var(--text3)"}}>🔒 protected</div>}
                              </div>
                            </div>
                          </td>
                          <td className="mono">{u.phone || "—"}</td>
                          <td style={{color:"var(--text2)",fontSize:12}}>{u.email || "—"}</td>
                          <td>{roleBadge(u.role)}</td>
                          <td>
                            <BranchBadge
                              branchName={u.branch_name || (u.branch_id ? `Branch #${u.branch_id}` : null)}
                              isMyBranch={!!u.is_my_branch}
                              compact
                            />
                          </td>
                          <td>
                            <span className={`badge ${u.status==="active"?"badge-active":u.status==="suspended"?"badge-suspended":"badge-pending"}`}>
                              {u.status}
                            </span>
                          </td>
                          <td className="mono" style={{fontSize:11,color:"var(--text3)"}}>
                            {u.created_at ? format(new Date(u.created_at),"MMM d, yyyy") : "—"}
                          </td>
                          <td>
                            <div style={{display:"flex",gap:4}}>
                              {canEdit   && <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>openEdit(u)} title="Edit"><Pencil size={13}/></button>}
                              {canDelete && <button className="btn btn-danger btn-icon btn-sm" onClick={()=>handleDelete(u)} title="Delete"><Trash2 size={13}/></button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <span>Showing {visible.length} of {total}</span>
              <div className="pagination-btns">
                <button className="btn btn-ghost btn-sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Prev</button>
                <span style={{padding:"0 8px",color:"var(--text2)"}}>Page {page} of {pages||1}</span>
                <button className="btn btn-ghost btn-sm" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create / Edit modal */}
      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{modal==="create"?"New User":"Edit User"}</div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label className="form-label">Full Name</label><input className="input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div>
                <div className="form-group"><label className="form-label">Phone</label><input className="input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))}/></div>
              </div>
              <div className="form-group"><label className="form-label">Email</label><input className="input" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/></div>
              {modal==="create" && <div className="form-group"><label className="form-label">Password</label><input className="input" type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))}/></div>}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="select" value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} disabled={isBranchMgr}>
                    {availableRoles.map(r=><option key={r} value={r}>{r.replace(/_/g," ")}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="select" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : modal==="create" ? "Create User" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}